import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Banner, Button, Card, Dialog, Field, FileDrop, PageHeader } from '@elysian/ui';
import { LOGO_ACCEPT } from '../../data/files';
import type { Contact, DocumentTemplate, LedgerDocument, TaxCode, TemplateLayout, TemplateSettings } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { InvoicePaper } from '../invoices/InvoicePaper';
import { DEFAULT_TEMPLATE } from '../invoices/InvoiceView';
import { bankLine, usePrintExtras } from '../invoices/printing';
import { iso, MERGE_FIELDS, unknownMergeFields } from '../invoices/shared';

const LAYOUTS: { key: TemplateLayout; label: string; note: string }[] = [
  { key: 'classic', label: 'Classic', note: 'Logo left, title right, ruled table' },
  { key: 'modern', label: 'Modern', note: 'Coloured band across the top' },
  { key: 'compact', label: 'Compact', note: 'Smaller type, fits long invoices on one page' },
];

const FONTS = [
  { key: 'dm_sans', label: 'DM Sans' },
  { key: 'ibm_plex', label: 'IBM Plex Sans' },
  { key: 'source_serif', label: 'Source Serif' },
  { key: 'lora', label: 'Lora' },
] as const;

// Optional fields on invoices (ledger._optional_fields), in plain words. Fields
// the screen doesn't show yet keep whatever value is stored.
const FIELDS: { key: string; label: string; dflt: boolean }[] = [
  { key: 'due_date', label: 'Due date', dflt: true },
  { key: 'customer_reference', label: 'Customer’s reference (PO)', dflt: true },
  { key: 'customer_vat_number', label: 'Customer’s VAT number', dflt: true },
  { key: 'contact_email', label: 'Customer’s email', dflt: true },
  { key: 'line_vat_amount', label: 'VAT amount on each line', dflt: false },
  { key: 'notes', label: 'Notes', dflt: true },
  { key: 'bank_details', label: 'Bank details for payment', dflt: true },
];

interface Loaded { template: DocumentTemplate | undefined; sample: LedgerDocument | undefined; contact: Contact | undefined; codes: TaxCode[];
  /** #43: every invoice layout (the default first) and the customers, to say who uses which. */
  layouts: DocumentTemplate[]; customers: Contact[] }

export function InvoiceDesign() {
  const { source, today } = useSession();
  const [version, setVersion] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const data = useAsync<Loaded>(async () => {
    const [templates, list, customers, codes] = await Promise.all([
      source.templates(), source.documents({ type: 'sales_invoice' }), source.contacts('customer'), source.taxCodes(iso(today())),
    ]);
    const latest = list.find((d) => d.status !== 'draft' && d.status !== 'void') ?? list[0];
    const sample = latest ? await source.document(latest.id) : undefined;
    const layouts = templates.filter((t) => t.kind === 'sales_invoice').sort((a, b) => Number(b.is_default) - Number(a.is_default) || a.name.localeCompare(b.name));
    return {
      template: templates.find((t) => t.kind === 'sales_invoice' && t.is_default),
      sample,
      contact: customers.find((c) => c.id === sample?.contact_id),
      codes, layouts, customers,
    };
  }, [source, version], { keep: true });

  if (data.state === 'loading') return <PageHeader title="Invoice design" subtitle="Loading…" />;
  if (data.state === 'error') return <><PageHeader title="Invoice design" /><Banner>Couldn’t load: {data.error.message}</Banner></>;
  const current = data.data.layouts.find((t) => t.id === selected) ?? data.data.template;
  return (
    <Designer key={`${current?.id ?? 'new'}:${current?.version ?? 0}:${current?.name ?? ''}`} {...data.data} template={current}
      picker={<LayoutPicker layouts={data.data.layouts} customers={data.data.customers} current={current}
        onPick={setSelected} onChanged={(id) => { if (id !== undefined) setSelected(id); setVersion((v) => v + 1); }} />} />
  );
}

/**
 * #43: which invoice layout is being designed: the default (everyone's) or a named one for particular
 * customers, chosen on a customer's page. New (a copy of this one), Rename, and Delete while unused.
 */
function LayoutPicker({ layouts, customers, current, onPick, onChanged }: {
  layouts: DocumentTemplate[]; customers: Contact[]; current: DocumentTemplate | undefined;
  onPick: (id: string) => void; onChanged: (select?: string | null) => void;
}) {
  const { source, can } = useSession();
  const toast = useToast((s) => s.show);
  const [dialog, setDialog] = useState<'new' | 'rename' | 'delete' | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const users = customers.filter((c) => current && c.invoice_template_id === current.id);
  const editable = can('templates.manage');

  async function act(work: () => Promise<string | null | undefined>, message: string) {
    setBusy(true);
    setError(null);
    try {
      const select = await work();
      setDialog(null);
      toast(message);
      onChanged(select);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginBottom: 16 }}><Card as="section" padding="14px 20px" aria-labelledby="d-which">
      <div className="el-spread" style={{ alignItems: 'flex-end' }}>
        <Field id="d-which" label="Invoice layout">
          <select id="d-which" className="el-input" value={current?.id ?? ''} onChange={(e) => onPick(e.target.value)} disabled={!layouts.length}>
            {!layouts.length && <option value="">Standard (everyone)</option>}
            {layouts.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (everyone else)' : ''}</option>)}
          </select>
        </Field>
        {editable && (
          <div className="el-row">
            <Button size="sm" disabled={!current} onClick={() => { setName(''); setError(null); setDialog('new'); }}>New layout</Button>
            {current && <Button size="sm" variant="ghost" onClick={() => { setName(current.name); setError(null); setDialog('rename'); }}>Rename</Button>}
            {current && !current.is_default && <Button size="sm" variant="danger" onClick={() => { setError(null); setDialog('delete'); }}>Delete</Button>}
          </div>
        )}
      </div>
      <p className="el-hint" style={{ margin: '8px 0 0' }}>
        {current?.is_default
          ? 'Customers without a layout of their own get this one.'
          : users.length
            ? `Used for ${users.map((c) => c.name).join(', ')}.`
            : 'No customer uses this layout yet: choose it on a customer’s page (Edit details → Invoice layout).'}
      </p>

      {dialog === 'new' && current && (
        <Dialog title="New invoice layout" onClose={() => setDialog(null)}
          actions={<><Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
            <Button variant="primary" disabled={busy || !name.trim()} onClick={() => void act(async () => (await source.createTemplate(name.trim(), current.id)).id, `${name.trim()} added: a copy of ${current.name} to change`)}>Add layout</Button></>}>
          <p style={{ margin: 0 }}>It starts as a copy of <strong>{current.name}</strong>. Change it here, then choose it for the customers it’s for.</p>
          <Field id="d-new-name" label="Name"><input id="d-new-name" className="el-input" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} placeholder="Hotel Group (PO on every invoice)" /></Field>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}
      {dialog === 'rename' && current && (
        <Dialog title={`Rename ${current.name}`} onClose={() => setDialog(null)}
          actions={<><Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
            <Button variant="primary" disabled={busy || !name.trim() || name.trim() === current.name} onClick={() => void act(async () => { await source.updateTemplate(current.id, { name: name.trim() }); return current.id; }, 'Layout renamed')}>Rename</Button></>}>
          <Field id="d-rename" label="Name"><input id="d-rename" className="el-input" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} /></Field>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}
      {dialog === 'delete' && current && (
        <Dialog title={`Delete ${current.name}?`} onClose={() => setDialog(null)}
          actions={<><Button variant="ghost" onClick={() => setDialog(null)}>Keep it</Button>
            <Button variant="danger" disabled={busy} onClick={() => void act(async () => { await source.deleteTemplate(current.id); return null; }, `${current.name} deleted`)}>Delete layout</Button></>}>
          <p style={{ margin: 0 }}>
            {users.length ? `${users.map((c) => c.name).join(', ')} will go back to the default layout. ` : ''}
            A layout already used on an issued invoice is kept with it, so it can’t be deleted; rename it instead.
          </p>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}
    </Card></div>
  );
}

function Designer({ template, sample, contact, codes, picker }: Loaded & { picker?: ReactNode }) {
  const { source, context, can } = useSession();
  const toast = useToast((s) => s.show);
  const start = template ?? { ...DEFAULT_TEMPLATE, version: 0 };
  const [layout, setLayout] = useState<TemplateLayout>(start.layout);
  const [settings, setSettings] = useState<TemplateSettings>(start.settings);
  const [savedAs, setSavedAs] = useState<{ layout: TemplateLayout; settings: TemplateSettings; version: number }>({ layout: start.layout, settings: start.settings, version: start.version });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [logosVersion, setLogosVersion] = useState(0);
  const logos = useAsync(() => source.brandAssets(), [source, logosVersion], { keep: true });
  const extras = usePrintExtras(settings);
  const withDetails = extras.accounts.filter((a) => a.details);
  const defaultAccount = withDetails.find((a) => a.details?.is_default);

  const brand = settings.brand ?? {};
  const fields = settings.fields ?? {};
  const footerIndex = (settings.blocks ?? []).findIndex((b) => b.zone === 'footer' && (b.type === 'terms' || b.type === 'text'));
  const footer = footerIndex >= 0 ? settings.blocks![footerIndex]!.text ?? '' : '';
  const unknown = unknownMergeFields(footer);
  const accentOk = /^#[0-9A-Fa-f]{6}$/.test(brand.accent ?? '');
  const title = settings.title ?? '';
  const dirty = useMemo(() => layout !== savedAs.layout || JSON.stringify(settings) !== JSON.stringify(savedAs.settings), [layout, settings, savedAs]);
  const valid = unknown.length === 0 && accentOk && title.length <= 40;
  const editable = can('templates.manage');

  const setBrand = (patch: Partial<NonNullable<TemplateSettings['brand']>>) => setSettings((s) => ({ ...s, brand: { ...s.brand, ...patch } }));
  const setField = (k: string, v: boolean) => setSettings((s) => ({ ...s, fields: { ...s.fields, [k]: v } }));
  const setFooter = (text: string) => setSettings((s) => {
    const blocks = [...(s.blocks ?? [])];
    if (footerIndex >= 0) blocks[footerIndex] = { ...blocks[footerIndex]!, text };
    else blocks.push({ type: 'terms', zone: 'footer', text });
    return { ...s, blocks: blocks.filter((b) => b.type !== 'terms' || (b.text ?? '').trim() !== '') };
  });

  async function uploadLogo(file: File) {
    setUploading(true);
    setLogoError(null);
    try {
      const a = await source.uploadBrandAsset(file, 'logo');
      setBrand({ logo_asset_id: a.id });
      setLogosVersion((v) => v + 1);
      toast('Logo added. Save the design to use it on invoices.');
    } catch (e) {
      setLogoError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  const setBankAccount = (code: string) => setSettings((s) => {
    const { bank_account_code: _old, ...rest } = s;
    return code ? { ...rest, bank_account_code: code } : rest;
  });

  async function save() {
    setBusy(true);
    setError(null);
    try {
      // A named layout is saved by its id; the default as before (#43).
      const t = template && !template.is_default
        ? await source.updateTemplate(template.id, { layout, settings })
        : await source.saveTemplate('sales_invoice', layout, settings);
      setSavedAs({ layout: t.layout, settings: t.settings, version: t.version });
      toast(`Saved as version ${t.version}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const previewDoc = sample ?? {
    doc_no: 'INV-0001', status: 'posted' as const, type: 'sales_invoice' as const, issue_date: '2026-10-01', due_date: '2026-10-31',
    reference: 'PO 1234', notes: null, currency: 'EUR', net_total: '1000.0000', tax_total: '135.0000', gross_total: '1135.0000', outstanding: '1135.0000',
    lines: [{ line_no: 1, description: 'Fire alarm service', quantity: '1', unit_price: '1000.00', account_code: '4010', tax_code: 'R135', net_amount: '1000.0000', tax_amount: '135.0000' }],
  };

  return (
    <>
      <PageHeader
        title="Invoice design"
        subtitle={`How invoices look as PDFs and email attachments. Version ${savedAs.version || 'not saved yet'}; posted invoices keep the version they were sent with.`}
        actions={
          <>
            <Button variant="ghost" disabled={!dirty || busy} onClick={() => { setLayout(savedAs.layout); setSettings(savedAs.settings); setError(null); }}>Undo changes</Button>
            <Button variant="primary" disabled={!dirty || !valid || busy || !editable} onClick={() => void save()}>Save design</Button>
          </>
        }
      />
      {picker}
      {error && <div className="el-error-box" role="alert" style={{ marginBottom: 16 }}>{error}</div>}
      <div className="el-design-layout">
        <div className="el-stack">
          <Card as="section" padding="18px 20px" aria-labelledby="d-layout">
            <h2 id="d-layout" className="el-h2" style={{ marginBottom: 12 }}>Layout</h2>
            <div className="el-choice-grid" role="radiogroup" aria-labelledby="d-layout">
              {LAYOUTS.map((l) => (
                <label key={l.key} className="el-choice" data-checked={layout === l.key}>
                  <input type="radio" name="layout" value={l.key} checked={layout === l.key} onChange={() => setLayout(l.key)} />
                  <span><strong>{l.label}</strong><span className="el-hint">{l.note}</span></span>
                </label>
              ))}
            </div>
          </Card>

          <Card as="section" padding="18px 20px" aria-labelledby="d-brand">
            <h2 id="d-brand" className="el-h2" style={{ marginBottom: 12 }}>Look</h2>
            <div className="el-form-grid">
              <Field id="d-title" label="Title" error={title.length > 40 ? 'Keep it to 40 characters' : null}>
                <input id="d-title" className="el-input" value={title} onChange={(e) => setSettings((s) => ({ ...s, title: e.target.value }))} />
              </Field>
              <Field id="d-accent" label="Accent colour" error={accentOk ? null : 'Use a hex colour such as #0F6E56'}>
                <div className="el-row" style={{ flexWrap: 'nowrap' }}>
                  <input type="color" aria-label="Pick accent colour" className="el-swatch" value={accentOk ? brand.accent : '#0F6E56'} onChange={(e) => setBrand({ accent: e.target.value.toUpperCase() })} />
                  <input id="d-accent" className="el-input" value={brand.accent ?? ''} onChange={(e) => setBrand({ accent: e.target.value })} />
                </div>
              </Field>
              <Field id="d-font" label="Font">
                <select id="d-font" className="el-input" value={brand.font ?? 'dm_sans'} onChange={(e) => setBrand({ font: e.target.value as typeof FONTS[number]['key'] })}>
                  {FONTS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </Field>
            </div>
          </Card>

          <Card as="section" padding="18px 20px" aria-labelledby="d-logo-h">
            <h2 id="d-logo-h" className="el-h2" style={{ marginBottom: 12 }}>Logo</h2>
            <div className="el-stack" style={{ gap: 12 }}>
              {extras.logoUrl
                ? <div className="el-logo-preview"><img src={extras.logoUrl} alt="Your logo" /></div>
                : <p className="el-muted" style={{ margin: 0 }}>{brand.logo_asset_id ? 'Loading the logo…' : 'No logo yet; invoices show your company’s initial instead.'}</p>}
              {editable && (
                <FileDrop label={uploading ? 'Uploading…' : brand.logo_asset_id ? 'Upload a new logo' : 'Upload a logo'} accept={LOGO_ACCEPT} disabled={uploading}
                  hint="PNG, JPEG or WebP up to 2 MB; a PNG with a transparent background prints best" onFiles={(f) => void uploadLogo(f[0]!)} />
              )}
              {logoError && <div className="el-error-box" role="alert">{logoError}</div>}
              <div className="el-form-grid">
                {logos.state === 'ready' && logos.data.length > 1 && (
                  <Field id="d-logo-pick" label="Logos you’ve uploaded">
                    <select id="d-logo-pick" className="el-input" value={brand.logo_asset_id ?? ''} onChange={(e) => setBrand({ logo_asset_id: e.target.value || undefined })}>
                      {logos.data.map((l, i) => (
                        <option key={l.id} value={l.id}>{`Logo ${logos.data.length - i} · ${l.width_px}×${l.height_px}${l.used_by.length ? ` · in ${l.used_by.join(', ')}` : ''}`}</option>
                      ))}
                    </select>
                  </Field>
                )}
                <Field id="d-logo" label="Position">
                  <select id="d-logo" className="el-input" value={brand.logo_position ?? 'left'} onChange={(e) => setBrand({ logo_position: e.target.value as 'left' | 'centre' | 'right' })}>
                    <option value="left">Left</option><option value="centre">Centre</option><option value="right">Right</option>
                  </select>
                </Field>
                <Field id="d-logo-size" label="Height on the page" hint={`${brand.logo_height_mm ?? 18} mm`}>
                  <input id="d-logo-size" type="range" min={10} max={40} step={1} value={brand.logo_height_mm ?? 18}
                    onChange={(e) => setBrand({ logo_height_mm: Number(e.target.value) })} />
                </Field>
              </div>
              {brand.logo_asset_id && editable && (
                <div><Button size="sm" variant="ghost" onClick={() => setBrand({ logo_asset_id: undefined })}>Don’t print a logo</Button></div>
              )}
            </div>
          </Card>

          <Card as="section" padding="18px 20px" aria-labelledby="d-bank">
            <h2 id="d-bank" className="el-h2" style={{ marginBottom: 12 }}>Bank details</h2>
            {extras.accounts.length > 0 && withDetails.length === 0 ? (
              <p className="el-muted" style={{ margin: 0 }}>
                No bank account has its IBAN entered yet, so invoices don’t say where to pay. <Link to="/bank-accounts">Add bank details</Link>.
              </p>
            ) : (
              <Field id="d-bank-acc" label="Account printed for payment"
                hint={extras.bank ? bankLine(extras.bank) : 'Bank details are switched off under What to show, or none are set'}>
                <select id="d-bank-acc" className="el-input" value={settings.bank_account_code ?? ''} onChange={(e) => setBankAccount(e.target.value)}>
                  <option value="">{defaultAccount ? `The default account (${defaultAccount.name})` : 'The default account'}</option>
                  {withDetails.map((a) => <option key={a.code} value={a.code}>{`${a.name} (${a.code})`}</option>)}
                </select>
              </Field>
            )}
          </Card>

          <Card as="section" padding="18px 20px" aria-labelledby="d-fields">
            <h2 id="d-fields" className="el-h2" style={{ marginBottom: 8 }}>What to show</h2>
            <p className="el-hint" style={{ margin: '0 0 8px' }}>Invoice number, dates, lines, VAT and totals are always shown; Revenue requires them.</p>
            <div className="el-check-grid">
              {FIELDS.map((f) => (
                <label key={f.key} className="el-check">
                  <input type="checkbox" checked={fields[f.key] ?? f.dflt} onChange={(e) => setField(f.key, e.target.checked)} />
                  {f.label}
                </label>
              ))}
            </div>
          </Card>

          <Card as="section" padding="18px 20px" aria-labelledby="d-footer">
            <h2 id="d-footer" className="el-h2" style={{ marginBottom: 12 }}>Footer text</h2>
            <Field id="d-footer-text" label="Payment terms or a thank-you"
              error={unknown.length ? `Not a merge field: ${unknown.map((u) => `{{${u}}}`).join(', ')}` : null}
              hint="Merge fields fill in per invoice, for example {{invoice.due_date}}">
              <textarea id="d-footer-text" className="el-input" rows={4} value={footer} onChange={(e) => setFooter(e.target.value)} />
            </Field>
            <details style={{ marginTop: 10 }}>
              <summary className="el-hint" style={{ cursor: 'pointer' }}>All merge fields</summary>
              <div className="el-token-list">
                {MERGE_FIELDS.filter((m) => !m.startsWith('statement.') && !m.startsWith('period')).map((m) => (
                  <button key={m} type="button" className="el-token" onClick={() => setFooter(`${footer}${footer && !footer.endsWith(' ') ? ' ' : ''}{{${m}}}`)}>{`{{${m}}}`}</button>
                ))}
              </div>
            </details>
          </Card>
        </div>

        <section aria-label="Preview" className="el-design-preview">
          <div className="el-spread" style={{ marginBottom: 10 }}>
            <h2 className="el-h2">Preview</h2>
            <span className="el-hint">{sample ? `Using ${sample.doc_no ?? 'your latest invoice'}` : 'Sample invoice'}</span>
          </div>
          <InvoicePaper doc={previewDoc} contact={contact ?? { name: 'Sample Customer Ltd', email: 'accounts@example.ie' }}
            org={context?.organisation} layout={layout} settings={settings} codes={codes} bank={extras.bank} logoUrl={extras.logoUrl} />
        </section>
      </div>
    </>
  );
}
